'use client';

import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface SubnetData {
  subnet: string;
  vlan: string;
  used: number;
  total: number;
  pct: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-surface-elevated border border-border rounded-xl p-3 shadow-xl text-sm">
      <p className="font-mono text-white font-bold mb-1">{d.subnet}</p>
      <p className="text-secondary-color">{d.vlan}</p>
      <p className="text-white mt-1">
        <span className="text-muted-color">Usati: </span>
        <span className="font-mono">{d.used}/{d.total}</span>
      </p>
      <p className={`font-bold mt-0.5 ${d.pct >= 90 ? 'text-red-400' : d.pct >= 80 ? 'text-amber-400' : 'text-sky-400'}`}>
        {d.pct.toFixed(1)}% utilizzato
      </p>
    </div>
  );
};

export default function SubnetUtilizationChart() {
  const [data, setData] = useState<SubnetData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubnets = async () => {
      try {
        const { data: subnets } = await supabase
          .from('subnets')
          .select('nome, cidr, ip_totali, ip_usati, area')
          .order('cidr');

        const mapped: SubnetData[] = (subnets || []).map((s: any) => {
          const total = s.ip_totali || 0;
          const used = s.ip_usati || 0;
          const pct = total > 0 ? Math.round((used / total) * 100 * 10) / 10 : 0;
          const vlanLabel = s.area ? s.area.charAt(0).toUpperCase() + s.area.slice(1) : '—';
          return { subnet: s.cidr, vlan: vlanLabel, used, total, pct };
        });
        setData(mapped);
      } catch {
        // silent fail — chart stays empty
      } finally {
        setLoading(false);
      }
    };

    fetchSubnets();

    const channel = supabase
      .channel('subnet_chart_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subnets' }, fetchSubnets)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-xl p-5 h-[300px] flex items-center justify-center">
        <svg className="animate-spin w-6 h-6 text-sky-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-xl p-5 h-[300px] flex items-center justify-center">
        <p className="text-secondary-color text-sm">Nessuna subnet configurata</p>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-white">Utilizzo Sottoreti</h2>
          <p className="text-sm text-secondary-color mt-0.5">Assegnazione IP su tutte le sottoreti</p>
        </div>
        <div className="flex items-center gap-3 text-sm text-secondary-color">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-red-500/70 inline-block" />
            Critico (&gt;90%)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-amber-500/70 inline-block" />
            Attenzione (&gt;80%)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-sky-500/70 inline-block" />
            Normale
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barSize={28}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 25% 18%)" vertical={false} />
          <XAxis
            dataKey="subnet"
            tick={{ fill: 'hsl(215 15% 40%)', fontSize: 11, fontFamily: 'IBM Plex Mono' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => v.split('/')[0].split('.').slice(2).join('.')}
          />
          <YAxis
            tick={{ fill: 'hsl(215 15% 40%)', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${v}%`}
            domain={[0, 100]}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(222 25% 18% / 0.5)' }} />
          <ReferenceLine y={85} stroke="hsl(38 92% 50% / 0.4)" strokeDasharray="4 4" />
          <ReferenceLine y={90} stroke="hsl(0 84% 60% / 0.4)" strokeDasharray="4 4" />
          <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
            {data.map((entry) => (
              <Cell
                key={`cell-subnet-${entry.subnet}`}
                fill={
                  entry.pct >= 90
                    ? 'hsl(0 84% 60% / 0.75)'
                    : entry.pct >= 80
                    ? 'hsl(38 92% 50% / 0.75)'
                    : 'hsl(199 89% 48% / 0.75)'
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}